import type { Direction } from '@tama/game-core';

export interface DirectionPadProps {
  onPress: (direction: Direction) => void;
  onRelease: (direction: Direction) => void;
  onInteract: () => void;
  disabled?: boolean;
}

const ARROWS: { direction: Direction; label: string; glyph: string }[] = [
  { direction: 'up', label: 'Walk up', glyph: '▲' },
  { direction: 'left', label: 'Walk left', glyph: '◀' },
  { direction: 'right', label: 'Walk right', glyph: '▶' },
  { direction: 'down', label: 'Walk down', glyph: '▼' },
];

/**
 * The touch controls.
 *
 * Deliberately *not* `data-focusable`: on a television the remote's own D-pad
 * already moves the pet, and a focusable on-screen pad would just be five more
 * things for the focus ring to get stuck in.
 *
 * Pointer down starts walking and pointer up stops it, so a held thumb walks
 * the way a held key does. `onPointerLeave` matters as much as `onPointerUp` —
 * a thumb that slides off the button never sends an up.
 *
 * Where each key sits is `data-key` plus a stylesheet rule, not an inline
 * `grid-area`. A phone held sideways wants the cross under the left thumb and
 * OK under the right one rather than a cross with OK in its middle, and an
 * inline style outranks the media query that would do it.
 */
export function DirectionPad({
  onPress,
  onRelease,
  onInteract,
  disabled = false,
}: DirectionPadProps) {
  return (
    <div className="dpad" role="group" aria-label="Move" data-testid="dpad">
      {ARROWS.map(({ direction, label, glyph }) => (
        <button
          key={direction}
          type="button"
          className="dpad__key"
          data-key={direction}
          aria-label={label}
          data-testid={`dpad-${direction}`}
          disabled={disabled}
          onPointerDown={(event) => {
            // Stops the press turning into a scroll or a text selection halfway
            // through a walk.
            event.preventDefault();
            onPress(direction);
          }}
          onPointerUp={() => onRelease(direction)}
          onPointerLeave={() => onRelease(direction)}
          onPointerCancel={() => onRelease(direction)}
        >
          {glyph}
        </button>
      ))}

      <button
        type="button"
        className="dpad__key dpad__key--ok"
        data-key="ok"
        aria-label="Look at what is in front"
        data-testid="dpad-ok"
        disabled={disabled}
        onClick={onInteract}
      >
        OK
      </button>
    </div>
  );
}
